import { SubmitHelpers } from '@area2k/use-form';
import { useCallback, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import EditAddress, { TypeEditorEnum } from '@/components/EditAddress';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import Form from '@/form';
import { useChangeWorkerAddressMutation } from '@/graphql';
import { handleMutationFormError } from '@/util/error';
import { WorkerDetail } from '@/services/workersV2API';

type Props = {
  worker: WorkerDetail;
  onClose: () => void;
  onSuccess: () => void;
};

type FormValues = {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  coords: {
    latitude: number;
    longitude: number;
  };
};

const ChangeAddressModal = ({ worker, onClose, onSuccess }: Props) => {
  const [initialValues, setInitialValues] = useState<FormValues>({
    addressLine1: worker.address?.address_line1 || '',
    addressLine2: worker.address?.address_line2 || null,
    city: worker.address?.city || '',
    state: worker.address?.state || '',
    zip: worker.address?.zip || '',
    coords: {
      latitude: worker.location?.lat || 0,
      longitude: worker.location?.lng || 0,
    },
  });

  const [changeWorkerAddress, { loading: isLoading }] =
    useChangeWorkerAddressMutation();

  const handleSubmit = useCallback(
    async (_values: FormValues, { setFormError }: SubmitHelpers) => {
      try {
        await changeWorkerAddress({
          variables: { workerId: String(worker.id), ...initialValues },
        });
        onSuccess();
        onClose();
      } catch (err: unknown) {
        handleMutationFormError(err, {
          setFormError,
          errorMap: {
            all: (gqlError) => ({
              title: gqlError.name,
              message: gqlError.message,
              status: 'danger',
            }),
          },
        });
      }
    },
    [worker.id, initialValues],
  );

  return (
    <Modal
      disableClickout
      size="sm"
      title="Change GravyWorker address"
      onRequestClose={onClose}
    >
      <Form initialValues={initialValues} onSubmit={handleSubmit}>
        <Card.Section>
          <EditAddress
            setValues={setInitialValues}
            typeEditor={TypeEditorEnum.FOR_WORKER}
            values={initialValues}
          />
        </Card.Section>
        <Card.Section>
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              id="btn-save"
              isLoading={isLoading}
              label="Save"
              type="submit"
            />
          </Stack>
        </Card.Section>
      </Form>
    </Modal>
  );
};

export default ChangeAddressModal;
